/**
 * Folder endpoints for the API contract.
 *
 * These endpoints manage user folders for organizing audio conversions.
 * The legacy API uses path arrays (e.g., ["Documents", "PDFs"]) while the new
 * database uses name + parentId. Handlers must convert between these formats.
 *
 * @see packages/core/api-contract/README.md for the full endpoint list
 */
import {
  HttpApiEndpoint,
  HttpApiGroup,
  OpenApi,
} from 'effect/unstable/httpapi';
import { Effect, Schema, SchemaTransformation } from 'effect';
import { UserAuthMiddleware } from './middleware';
import {
  BadRequestError,
  ConflictError,
  NotFoundError,
  InternalServerError,
} from './errors';

// Import schemas from legacy client (these define the exact shape clients expect)
import {
  GetListFolders200,
  PostCreateFolderRequest,
  PostCreateFolder200,
  GetGetFolder200,
  PatchUpdateFolderRequest,
  PatchUpdateFolder200,
  DeleteDeleteFolder200,
} from './legacy-client.gen';

// URL params come as strings and need to be transformed to booleans
// DeleteDeleteFolderParams has a boolean field, so we need a string-compatible version
const BooleanFromString = Schema.String.pipe(
  Schema.decodeTo(
    Schema.Boolean,
    SchemaTransformation.transform({
      decode: (s) => s === 'true',
      encode: (b) => (b ? 'true' : 'false'),
    }),
  ),
);

const DeleteFolderUrlParams = Schema.Struct({
  recursive: Schema.optional(BooleanFromString),
});

export const MAX_AUDIO_CONVERSIONS_PER_FOLDER_UPDATE = 500;

const UpdateFolderRequest = Schema.Struct({
  ...PatchUpdateFolderRequest.fields,
  audio_conversion_ids: Schema.NullOr(
    Schema.Array(Schema.String).pipe(
      Schema.check(Schema.isMaxLength(MAX_AUDIO_CONVERSIONS_PER_FOLDER_UPDATE)),
    ),
  ).pipe(Schema.withDecodingDefaultType(Effect.succeed(null))),
}).annotate({ identifier: 'UpdateFolderRequest' });

/**
 * GET /v2/folders
 * List all folders for the current user.
 * Not paginated since users have a folder limit.
 */
const listFoldersEndpoint = HttpApiEndpoint.get('ListFolders', '/', {
  success: GetListFolders200,
  error: InternalServerError,
})
  .middleware(UserAuthMiddleware)

  .annotate(OpenApi.Summary, 'List all folders')
  .annotate(
    OpenApi.Description,
    'List all folders for the current user. Not paginated since users have a folder limit.',
  );

/**
 * POST /v2/folders
 * Create a new folder at the specified path.
 * Optionally move audio items to the new folder.
 */
const createFolderEndpoint = HttpApiEndpoint.post('CreateFolder', '/', {
  payload: PostCreateFolderRequest,
  success: PostCreateFolder200,
  error: [BadRequestError, ConflictError, InternalServerError],
})
  .middleware(UserAuthMiddleware)

  .annotate(OpenApi.Summary, 'Create a new folder')
  .annotate(
    OpenApi.Description,
    'Create a new folder at the specified path. Parent folders must already exist. Optionally move audio items to the new folder.',
  );

/**
 * GET /v2/folders/:folderId
 * Get folder details including child folders and audio items.
 * Use "root" as folderId to get the root folder.
 */
const getFolderEndpoint = HttpApiEndpoint.get('GetFolder', '/:folderId', {
  params: Schema.Struct({ folderId: Schema.String }),
  success: GetGetFolder200,
  error: [BadRequestError, NotFoundError, InternalServerError],
})
  .middleware(UserAuthMiddleware)

  .annotate(OpenApi.Summary, 'Get folder details')
  .annotate(
    OpenApi.Description,
    'Get details for a single folder including child folders and audio items. Use "root" as folderId to get the root folder.',
  );

/**
 * PATCH /v2/folders/:folderId
 * Update a folder - rename it or move audio items to it.
 * Use "root" as folderId to move items to the root folder.
 */
const updateFolderEndpoint = HttpApiEndpoint.patch(
  'UpdateFolder',
  '/:folderId',
  {
    params: Schema.Struct({ folderId: Schema.String }),
    payload: UpdateFolderRequest,
    success: PatchUpdateFolder200,
    // A missing folder is a typed 404 (matches GetFolder + the legacy Python
    // `get_by_id` → NotFound/404; the pre-cutover TS port wrongly returned 400).
    error: [BadRequestError, ConflictError, NotFoundError, InternalServerError],
  },
)
  .middleware(UserAuthMiddleware)
  .annotate(OpenApi.Summary, 'Update a folder')
  .annotate(
    OpenApi.Description,
    'Update folder path (rename/move) or move audio items to this folder. Use "root" as folderId to move items to the root folder.',
  );

/**
 * DELETE /v2/folders/:folderId
 * Delete a folder.
 * By default moves contents to parent folder (safe delete).
 * Use ?recursive=true to delete contents as well.
 */
const deleteFolderEndpoint = HttpApiEndpoint.delete(
  'DeleteFolder',
  '/:folderId',
  {
    params: Schema.Struct({ folderId: Schema.String }),
    query: DeleteFolderUrlParams,
    success: DeleteDeleteFolder200,
    // A missing folder is a typed 404 (matches GetFolder + the legacy Python
    // `get_by_id` → NotFound/404; the pre-cutover TS port wrongly returned 400).
    error: [BadRequestError, NotFoundError, InternalServerError],
  },
)
  .middleware(UserAuthMiddleware)
  .annotate(OpenApi.Summary, 'Delete a folder')
  .annotate(
    OpenApi.Description,
    'Delete a folder. By default moves contents to parent folder. Use ?recursive=true to delete the folder and all its contents.',
  );

export const folderGroup = HttpApiGroup.make('Folder')
  .add(listFoldersEndpoint)
  .add(createFolderEndpoint)
  .add(getFolderEndpoint)
  .add(updateFolderEndpoint)
  .add(deleteFolderEndpoint)
  .prefix('/v2/folders');
